import { useReactFlow, NodeProps } from '@xyflow/react'
import DOMPurify from 'dompurify'
import {
  FileUp,
  File,
  Trash,
  RefreshCw,
  Upload,
  X,
  Folder,
  FilePlus,
  FileText,
  Download,
  PanelRight,
  PanelRightClose,
  HelpCircle,
  Copy,
} from 'lucide-react'
import React, { useState, useEffect, useRef, memo, useMemo } from 'react'
import Markdown from 'react-markdown'
import { useParams } from 'react-router'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useUser } from '@/context/UserContext'
import useDataSettings from '@/hooks/useDataSettings'
import { useFileStore } from '@/stores/fileStore'
import styles from '@/styles/NodeFileMiner.module.css'
import {
  AICommand,
  AICommandType,
  AICommandStatus,
  createAICommand,
  isCommandComplete,
  isCommandError,
  isCommandInProgress,
  createNodeCommandPayload,
  useAICommandState,
} from '@/utils/aiCommandUtils'
import NodeBaseActions from '../base/base-actions/NodeBaseActions'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'

interface NodeFileMinerData {
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  statusMessage?: string
  blobIds?: string[] // Blob names to process
  selectedFiles?: {
    id: string
    filename: string
    contentType?: string
    size?: number
    blobName?: string // Add blobName explicitly
  }[]
  userPrompt?: string
  systemPrompt?: string
  analysisResult?: string
  selectedPresetPrompts?: string[]
  aiSuggestions?: string[]
  contentMarkdown?: string
  showFileExplorer?: boolean
  showSideBySide?: boolean
  documentRequest?: {
    document: string
    processing: boolean
    processedAt: string
    timestamp: number
    'sysagent-cmd': string
    'sysagent-response': string
    [key: string]: any
  }
  userId?: string
}

const PRESET_PROMPTS = [
  // Common Analysis
  {
    id: 'summary',
    label: 'Summary',
    prompt: 'Summarize the key points from these documents concisely',
    group: 'common',
  },
  {
    id: 'compare',
    label: 'Compare & Contrast',
    prompt: 'Compare and contrast the information in these documents...',
    group: 'common',
  },
  {
    id: 'analyze',
    label: 'Deep Analysis',
    prompt: 'Perform a deep analysis of these documents...',
    group: 'common',
  },
  {
    id: 'proscons',
    label: 'Pros & Cons',
    prompt: 'List the pros and cons of the approaches or topics...',
    group: 'common',
  },
  {
    id: 'extract_entities',
    label: 'Extract Entities',
    prompt: 'Extract all key entities...',
    group: 'common',
  },
  {
    id: 'timeline',
    label: 'Timeline',
    prompt: 'Create a chronological timeline...',
    group: 'common',
  },
  {
    id: 'questions',
    label: 'Generate Questions',
    prompt: 'Generate a list of important questions...',
    group: 'common',
  },
  {
    id: 'key_concepts',
    label: 'Key Concepts',
    prompt: 'Identify and explain the key concepts...',
    group: 'common',
  },

  // Excel Sheets
  {
    id: 'excel_data_patterns',
    label: 'Data Patterns',
    prompt:
      'Analyze data patterns, trends, and correlations in the spreadsheet data',
    group: 'excel',
  },
  {
    id: 'excel_financial_analysis',
    label: 'Financial Analysis',
    prompt:
      'Extract financial metrics, calculations, and perform financial analysis',
    group: 'excel',
  },
  {
    id: 'excel_pivot_summary',
    label: 'Pivot Analysis',
    prompt: 'Create pivot table summaries and cross-tabulations of the data',
    group: 'excel',
  },
  {
    id: 'excel_data_quality',
    label: 'Data Quality Check',
    prompt: 'Identify data inconsistencies, missing values, and quality issues',
    group: 'excel',
  },
  {
    id: 'excel_formulas',
    label: 'Formula Analysis',
    prompt: 'Extract and explain all formulas and calculations used',
    group: 'excel',
  },
  {
    id: 'excel_markdown_tables',
    label: 'Extract as Markdown Tables',
    prompt:
      'Extract each sheet as a markdown table enclosed in markdown code blocks, preserving structure and formatting',
    group: 'excel',
  },

  // PowerPoint Presentations
  {
    id: 'ppt_key_messages',
    label: 'Key Messages',
    prompt: 'Extract the main messages and takeaways from each slide',
    group: 'powerpoint',
  },
  {
    id: 'ppt_slide_structure',
    label: 'Presentation Structure',
    prompt: 'Analyze the presentation flow and logical structure',
    group: 'powerpoint',
  },
  {
    id: 'ppt_visual_elements',
    label: 'Visual Analysis',
    prompt:
      'Describe charts, diagrams, and visual elements with their insights',
    group: 'powerpoint',
  },
  {
    id: 'ppt_action_items',
    label: 'Action Items',
    prompt: 'Extract action items, recommendations, and next steps',
    group: 'powerpoint',
  },
  {
    id: 'ppt_speaker_notes',
    label: 'Speaker Notes',
    prompt: 'Extract and summarize speaker notes and additional context',
    group: 'powerpoint',
  },
  {
    id: 'ppt_slide_extraction',
    label: 'Individual Slide Extraction',
    prompt:
      'Extract content from each slide individually with slide numbers, titles, bullet points, and detailed descriptions',
    group: 'powerpoint',
  },

  // Video Files
  {
    id: 'video_transcript',
    label: 'Transcript Summary',
    prompt: 'Extract and summarize spoken content and dialogue',
    group: 'video',
  },
  {
    id: 'video_visual_analysis',
    label: 'Visual Content',
    prompt: 'Describe key visual scenes, objects, and activities shown',
    group: 'video',
  },
  {
    id: 'video_timeline',
    label: 'Timeline Events',
    prompt: 'Create a timeline of key events and moments in the video',
    group: 'video',
  },
  {
    id: 'video_instructional',
    label: 'Instructions/Steps',
    prompt: 'Extract step-by-step instructions or procedures shown',
    group: 'video',
  },
  {
    id: 'video_metadata',
    label: 'Technical Details',
    prompt: 'Extract technical metadata, duration, quality, and specifications',
    group: 'video',
  },
  {
    id: 'video_mood',
    label: 'Mood Analysis',
    prompt:
      'Analyze the overall mood, atmosphere, and emotional tone throughout the video',
    group: 'video',
  },
  {
    id: 'video_activity',
    label: 'Activity Detection',
    prompt:
      'Identify and describe all activities, actions, and movements happening in the video',
    group: 'video',
  },

  // Audio Files
  {
    id: 'audio_transcript',
    label: 'Audio Transcript',
    prompt: 'Transcribe and summarize all spoken content',
    group: 'audio',
  },
  {
    id: 'audio_speakers',
    label: 'Speaker Analysis',
    prompt: 'Identify different speakers and their contributions',
    group: 'audio',
  },
  {
    id: 'audio_sentiment',
    label: 'Sentiment Analysis',
    prompt: 'Analyze tone, emotion, and sentiment in the audio',
    group: 'audio',
  },
  {
    id: 'audio_topics',
    label: 'Topic Extraction',
    prompt: 'Extract main topics and themes discussed',
    group: 'audio',
  },
  {
    id: 'audio_timestamps',
    label: 'Key Moments',
    prompt: 'Identify timestamps of important moments and topics',
    group: 'audio',
  },

  // Image Files
  {
    id: 'image_description',
    label: 'Visual Description',
    prompt: 'Provide detailed description of all visual elements',
    group: 'image',
  },
  {
    id: 'image_text_extraction',
    label: 'Text Extraction (OCR)',
    prompt: 'Extract and transcribe all text visible in the images',
    group: 'image',
  },
  {
    id: 'image_objects',
    label: 'Object Detection',
    prompt: 'Identify and catalog all objects, people, and elements',
    group: 'image',
  },
  {
    id: 'image_technical',
    label: 'Technical Analysis',
    prompt: 'Analyze image quality, composition, and technical properties',
    group: 'image',
  },
  {
    id: 'image_data_viz',
    label: 'Chart/Graph Analysis',
    prompt: 'Extract data and insights from charts, graphs, and diagrams',
    group: 'image',
  },
  {
    id: 'image_mood',
    label: 'Mood Analysis',
    prompt:
      'Analyze the mood, atmosphere, and emotional tone conveyed by the image',
    group: 'image',
  },
  {
    id: 'image_activity',
    label: 'Activity Detection',
    prompt:
      'Identify and describe activities, actions, or events depicted in the image',
    group: 'image',
  },

  // Text Files
  {
    id: 'text_structure',
    label: 'Document Structure',
    prompt: 'Analyze document structure, sections, and organization',
    group: 'text',
  },
  {
    id: 'text_citations',
    label: 'Citations & References',
    prompt: 'Extract all citations, references, and bibliographic information',
    group: 'text',
  },
  {
    id: 'text_language',
    label: 'Language Analysis',
    prompt: 'Analyze writing style, tone, and linguistic patterns',
    group: 'text',
  },
  {
    id: 'text_keywords',
    label: 'Keywords & Phrases',
    prompt: 'Extract important keywords, phrases, and terminology',
    group: 'text',
  },
  {
    id: 'text_formatting',
    label: 'Formatting Analysis',
    prompt: 'Analyze formatting, headings, lists, and document layout',
    group: 'text',
  },
  {
    id: 'text_personality',
    label: 'Personality Analysis',
    prompt:
      'Analyze the personality traits and characteristics revealed through the writing style, word choices, and communication patterns',
    group: 'text',
  },
  {
    id: 'text_psychprofile',
    label: 'Psychological Profile',
    prompt:
      'Create a psychological profile based on the text, identifying cognitive patterns, emotional tendencies, and behavioral indicators',
    group: 'text',
  },
  {
    id: 'text_mood',
    label: 'Mood Analysis',
    prompt:
      'Analyze the overall mood, emotional state, and psychological atmosphere conveyed in the text',
    group: 'text',
  },

  // Specialized
  {
    id: 'specialized_legal',
    label: 'Legal Document Analysis',
    prompt: 'Extract legal terms, clauses, obligations, and key legal points',
    group: 'specialized',
  },
  {
    id: 'specialized_medical',
    label: 'Medical/Scientific Analysis',
    prompt: 'Extract medical terms, procedures, findings, and scientific data',
    group: 'specialized',
  },
  {
    id: 'specialized_technical',
    label: 'Technical Documentation',
    prompt: 'Extract technical specifications, procedures, and requirements',
    group: 'specialized',
  },
  {
    id: 'specialized_research',
    label: 'Research Paper Analysis',
    prompt: 'Extract methodology, findings, conclusions, and research insights',
    group: 'specialized',
  },
  {
    id: 'specialized_compliance',
    label: 'Compliance Check',
    prompt: 'Check for compliance requirements, regulations, and standards',
    group: 'specialized',
  },
  {
    id: 'specialized_risk',
    label: 'Risk Assessment',
    prompt:
      'Identify potential risks, vulnerabilities, and mitigation strategies',
    group: 'specialized',
  },
]

const PROMPT_GROUPS = {
  common: {
    label: 'Common Analysis',
    description: 'General-purpose analysis for any file type',
  },
  excel: {
    label: 'Excel Sheets',
    description: 'Specialized extraction for spreadsheets and data files',
  },
  powerpoint: {
    label: 'PowerPoint Presentations',
    description: 'Extract insights from presentation slides',
  },
  video: {
    label: 'Video Files',
    description: 'Analyze video content and extract information',
  },
  audio: {
    label: 'Audio Files',
    description: 'Process audio content and speech',
  },
  image: {
    label: 'Image Files',
    description: 'Analyze images, charts, and visual content',
  },
  text: {
    label: 'Text Documents',
    description: 'Advanced text document analysis',
  },
  specialized: {
    label: 'Specialized Analysis',
    description: 'Domain-specific analysis for professional use',
  },
}

const DEFAULT_SYSTEM_PROMPT =
  'You are an expert document analyst with specialized capabilities across multiple file types and analysis domains. Provide comprehensive, detailed analysis with medium to long responses containing vast and useful information.'

const NodeFileMiner: React.FC<NodeProps & { data: NodeFileMinerData }> = (
  props
) => {
  const { id, data, selected } = props

  // Add logging to see what React Flow is passing
  useEffect(() => {
    console.log('🔍 NodeFileMiner props received:', {
      nodeId: id,
      selected,
      selectedType: typeof selected,
      allProps: Object.keys(props),
      timestamp: new Date().toISOString(),
    })
  }, [id, selected, props])

  const { projectId } = useParams()
  const { updateNodeData } = useReactFlow()
  const user = useUser()
  const {
    files,
    fetchFiles,
    uploadFile,
    uploadStandaloneFile,
    deleteFile,
    downloadFile,
    setProjectId,
  } = useFileStore()

  // Local state - these are the source of truth for UI
  const [selectedFiles, setSelectedFiles] = useState<
    {
      id: string
      filename: string
      contentType?: string
      size?: number
      blobName?: string
    }[]
  >(data.selectedFiles || [])
  const [userPrompt, setUserPrompt] = useState(data.userPrompt || '')
  const [systemPrompt, setSystemPrompt] = useState(
    data.systemPrompt || DEFAULT_SYSTEM_PROMPT
  )
  const [selectedPresetPrompts, setSelectedPresetPrompts] = useState<string[]>(
    data.selectedPresetPrompts || []
  )
  const [analysisResult, setAnalysisResult] = useState(
    data.analysisResult || ''
  )
  const [aiSuggestions, setAiSuggestions] = useState<string[]>(
    data.aiSuggestions || []
  )
  const [statusMessage, setStatusMessage] = useState(data.statusMessage || '')
  const [isLocked, setIsLocked] = useState(data.isLocked || false)
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false)
  const [minimizeLevel, setMinimizeLevel] = useState<
    'normal' | 'medium' | 'minimum'
  >('normal')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState(data.label || '')
  const [activeTab, setActiveTab] = useState<
    'extraction' | 'results' | 'suggestions'
  >('extraction')
  const [showFileExplorer, setShowFileExplorer] = useState(
    data.showFileExplorer || false
  )
  const [showSideBySide, setShowSideBySide] = useState(
    data.showSideBySide || false
  )
  const [dragActive, setDragActive] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  )
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [showHelpDialog, setShowHelpDialog] = useState(false)
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails)
      ? data.assignedEmails
      : data.assignedEmails
        ? data.assignedEmails.trim().split(/\s+/).filter(Boolean)
        : []
  )
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] =
    useState(false)
  const [copyFeedback, setCopyFeedback] = useState<string>('')

  const titleInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null)

  const isProcessingRef = useRef(false)
  const fileAnalysisState = useAICommandState(
    data.documentRequest,
    AICommandType.FILEMINER_PROCESS
  )
  const suggestionsState = useAICommandState(
    data.documentRequest,
    AICommandType.FILEMINER_SUGGESTIONS
  )
  const [isGenerating, setIsGenerating] = useState(false)

  const currentStatus =
    isGenerating || isUploading
      ? 'loading'
      : data.documentRequest &&
        isCommandComplete(
          data.documentRequest,
          AICommandType.FILEMINER_PROCESS
        )
        ? 'success'
        : data.documentRequest &&
          isCommandError(
            data.documentRequest,
            AICommandType.FILEMINER_PROCESS
          )
          ? 'error'
          : ''

  // Compute status for NodeBaseContainer
  const nodeStatus = useMemo(() => {
    if (currentStatus === 'loading') return 'loading'
    if (currentStatus === 'error') return 'error'
    if (currentStatus === 'success') return 'success'
    return 'initial'
  }, [currentStatus])

  // Downstream sync: Update local state when shared state changes
  useEffect(() => {
    if (data.selectedFiles !== undefined) setSelectedFiles(data.selectedFiles)
  }, [data.selectedFiles])

  useEffect(() => {
    if (data.userPrompt !== undefined) setUserPrompt(data.userPrompt)
  }, [data.userPrompt])

  useEffect(() => {
    if (data.systemPrompt !== undefined) setSystemPrompt(data.systemPrompt)
  }, [data.systemPrompt])

  useEffect(() => {
    if (data.selectedPresetPrompts !== undefined)
      setSelectedPresetPrompts(data.selectedPresetPrompts)
  }, [data.selectedPresetPrompts])

  useEffect(() => {
    if (data.analysisResult !== undefined)
      setAnalysisResult(data.analysisResult)
  }, [data.analysisResult])

  useEffect(() => {
    if (data.aiSuggestions !== undefined) setAiSuggestions(data.aiSuggestions)
  }, [data.aiSuggestions])

  useEffect(() => {
    if (data.statusMessage !== undefined) setStatusMessage(data.statusMessage)
  }, [data.statusMessage])

  useEffect(() => {
    if (data.isMinimized !== undefined) setIsMinimized(data.isMinimized)
  }, [data.isMinimized])

  useEffect(() => {
    if (data.isLocked !== undefined) setIsLocked(data.isLocked)
  }, [data.isLocked])

  useEffect(() => {
    if (data.label !== undefined) setEditingTitleValue(data.label)
  }, [data.label])

  useEffect(() => {
    if (data.showFileExplorer !== undefined)
      setShowFileExplorer(data.showFileExplorer)
  }, [data.showFileExplorer])

  useEffect(() => {
    if (data.showSideBySide !== undefined)
      setShowSideBySide(data.showSideBySide)
  }, [data.showSideBySide])

  useEffect(() => {
    if (data.assignedEmails !== undefined) {
      setAssignedEmails(
        Array.isArray(data.assignedEmails)
          ? data.assignedEmails
          : data.assignedEmails
            ? data.assignedEmails.trim().split(/\s+/).filter(Boolean)
            : []
      )
    }
  }, [data.assignedEmails])

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' })
    } else {
      setMinimizeLevel(data.minimizeLevel)
      setIsMinimized(data.minimizeLevel !== 'normal')
    }
  }, [data.minimizeLevel, id, data, updateNodeData])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (selectedFiles.length > 0) {
      minifiedInfo.files = `${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`
    }

    if (userPrompt && userPrompt.trim()) {
      minifiedInfo.prompt =
        userPrompt.substring(0, 50) + (userPrompt.length > 50 ? '...' : '')
    }

    if (selectedPresetPrompts.length > 0) {
      minifiedInfo.presets = `${selectedPresetPrompts.length} preset${selectedPresetPrompts.length !== 1 ? 's' : ''}`
    }

    if (analysisResult) {
      minifiedInfo.analyzed = 'Yes'
    }

    if (aiSuggestions && aiSuggestions.length > 0) {
      minifiedInfo.suggestions = `${aiSuggestions.length} suggestion${aiSuggestions.length !== 1 ? 's' : ''}`
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      syncNodeData({ minifiedInfo })
    }
  }, [
    selectedFiles.length,
    userPrompt,
    selectedPresetPrompts.length,
    analysisResult,
    aiSuggestions?.length,
    data.minifiedInfo,
  ])

  // Project ID setup - this is acceptable since it's initialization, not responding to shared state
  useEffect(() => {
    if (projectId) {
      setProjectId(projectId)
    } else if (data.projectId) {
      setProjectId(data.projectId)
    } else {
      console.warn('No projectId available for NodeFileMiner')
      // This is user-initiated error state, acceptable to sync
      const errorMsg =
        'Error: No project context available. Please set a project ID.'
      syncNodeData({ statusMessage: errorMsg })
    }
  }, [projectId, data.projectId, setProjectId])

  useEffect(() => {
    setIsGenerating(
      fileAnalysisState.isProcessing || suggestionsState.isProcessing
    )
  }, [fileAnalysisState.isProcessing, suggestionsState.isProcessing])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  const syncNodeData = (newData: Partial<NodeFileMinerData>) => {
    const updatedData = { ...data, ...newData }
    updateNodeData(id, updatedData)
  }

  // User action handlers - these can call syncNodeData
  const handleLock = () => {
    const newState = !isLocked
    setIsLocked(newState)
    syncNodeData({ isLocked: newState })
  }

  const handleMinimize = () => {
    const newMinState = !isMinimized
    setIsMinimized(newMinState)
    setMinimizeLevel(newMinState ? 'medium' : 'normal')
    syncNodeData({
      isMinimized: newMinState,
      minimizeLevel: newMinState ? 'medium' : 'normal',
    })
  }

  const handleMinimizeLevelChange = (
    level: 'normal' | 'medium' | 'minimum'
  ) => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    syncNodeData({
      minimizeLevel: level,
      isMinimized: level !== 'normal',
    })
  }

  const confirmDelete = (item) => {
    setItemToDelete(item)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirmed = () => {
    if (itemToDelete && (window as any).__flowDeleteNode) {
      ; (window as any).__flowDeleteNode(id)
    }
    setShowDeleteDialog(false)
    setItemToDelete(null)
  }

  const handleDuplicate = () => {
    if ((window as any).__flowDuplicateNode) {
      ; (window as any).__flowDuplicateNode(id)
    }
  }

  const handleTitleSave = () => {
    syncNodeData({ label: editingTitleValue })
    setIsEditingTitle(false)
  }

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false)
      setEditingTitleValue(data.label || '')
    }
  }

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  const toggleFileExplorer = () => {
    const newState = !showFileExplorer
    syncNodeData({ showFileExplorer: newState })
  }

  const toggleSideBySide = () => {
    const newState = !showSideBySide
    syncNodeData({ showSideBySide: newState })
  }

  const handleSelectProjectFile = (file) => {
    if (!selectedFiles.some((f) => f.id === file.id)) {
      const blobName = file.url
        ? new URL(file.url).pathname.split('/').pop()
        : file.blobName || file.id

      const updatedFiles = [
        ...selectedFiles,
        {
          id: file.id,
          filename: file.filename,
          contentType: file.contentType,
          size: file.size,
          blobName: blobName,
        },
      ]

      syncNodeData({
        selectedFiles: updatedFiles,
        blobIds: updatedFiles.map((f) => f.blobName || f.id),
        showFileExplorer: false,
      })
    }
  }

  const handleRemoveFile = (fileId: string) => {
    const updatedFiles = selectedFiles.filter((f) => f.id !== fileId)

    const removedFile = selectedFiles.find((f) => f.id === fileId)
    const newStatusMessage = `Removed ${removedFile?.filename} from selection.`

    syncNodeData({
      selectedFiles: updatedFiles,
      blobIds: updatedFiles.map((f) => f.blobName || f.id),
      statusMessage: newStatusMessage,
    })
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileArray = Array.from(e.target.files)
      handleUploadedFiles(fileArray)
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fileArray = Array.from(e.dataTransfer.files)
      handleUploadedFiles(fileArray)
    }
  }

  const handleUploadedFiles = async (files: File[]) => {
    setIsUploading(true)
    setUploadedFiles((prevFiles) => [...prevFiles, ...files])

    const projectId = useFileStore.getState().currentProjectId
    if (!projectId) {
      const errorMsg =
        'Error: No project ID set. Please ensure a project is selected.'
      syncNodeData({ statusMessage: errorMsg })
      setIsUploading(false)
      return
    }

    const effectiveUserId = user?.id || user?.email
    if (!effectiveUserId) {
      const errorMsg = 'Error: User information missing. Please log in.'
      setStatusMessage(errorMsg)
      syncNodeData({ statusMessage: errorMsg })
      setIsUploading(false)
      return
    }

    const uploadingMsg = `Uploading ${files.length} file(s)...`
    syncNodeData({ statusMessage: uploadingMsg })

    const uploadedServerFiles = []
    let errorOccurred = false

    for (const file of files) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('userId', effectiveUserId)
        formData.append('projectId', projectId)

        const uploadedFile = await uploadStandaloneFile(formData)
        if (!uploadedFile?.blobName) {
          throw new Error(
            `Upload failed for ${file.name}: No blobName returned`
          )
        }

        uploadedServerFiles.push({
          id: uploadedFile.id,
          filename: uploadedFile.filename,
          contentType: uploadedFile.contentType,
          size: uploadedFile.size,
          blobName: uploadedFile.blobName,
        })
      } catch (error) {
        errorOccurred = true
        console.error(`Error uploading ${file.name}:`, error)
        const errorMsg = `Error uploading ${file.name}: ${error.message || 'Unknown error'}`
        syncNodeData({ statusMessage: errorMsg })
      }
    }

    if (uploadedServerFiles.length > 0) {
      const finalUpdatedFiles = [
        ...selectedFiles.filter((f) => !f.id.startsWith('local-')),
        ...uploadedServerFiles,
      ]
      setSelectedFiles(finalUpdatedFiles)

      const successMsg = errorOccurred
        ? 'Some files failed to upload.'
        : `Successfully uploaded ${uploadedServerFiles.length} file(s).`
      syncNodeData({
        selectedFiles: finalUpdatedFiles,
        blobIds: finalUpdatedFiles.map((f) => f.blobName),
        statusMessage: successMsg,
      })
    } else {
      const failMsg = 'No files uploaded successfully.'
      syncNodeData({ statusMessage: failMsg })
    }

    setIsUploading(false)
    fetchFiles()
  }

  const togglePresetPrompt = (promptId: string) => {
    if (isLocked) return

    let newSelected: string[]
    if (selectedPresetPrompts.includes(promptId)) {
      newSelected = selectedPresetPrompts.filter((id) => id !== promptId)
    } else {
      newSelected = [...selectedPresetPrompts, promptId]
    }

    const selectedPromptTexts = PRESET_PROMPTS.filter((prompt) =>
      newSelected.includes(prompt.id)
    ).map((prompt) => prompt.prompt)

    if (selectedPromptTexts.length > 0) {
      const combinedPrompt = selectedPromptTexts.join('. ') + '.'
      syncNodeData({
        selectedPresetPrompts: newSelected,
        userPrompt: combinedPrompt,
      })
    } else {
      syncNodeData({ selectedPresetPrompts: newSelected })
    }
  }

  const handleProcessFiles = async () => {
    if (selectedFiles.length === 0) {
      const errorMsg = 'Error: Please select at least one file to analyze.'
      syncNodeData({ statusMessage: errorMsg })
      return
    }

    if (!userPrompt.trim()) {
      const errorMsg = 'Error: Please enter a prompt or select preset prompts.'
      syncNodeData({ statusMessage: errorMsg })
      return
    }

    if (isUploading) {
      const errorMsg = 'Error: Please wait for all files to finish uploading.'
      syncNodeData({ statusMessage: errorMsg })
      return
    }

    const effectiveUserId = user?.id || user?.email
    if (!effectiveUserId) {
      const errorMsg = 'Error: User information missing. Please log in.'
      syncNodeData({ statusMessage: errorMsg })
      return
    }

    const preparingMsg = 'Preparing files for analysis...'
    syncNodeData({ statusMessage: preparingMsg })

    try {
      await fetchFiles()
      const existingFileIds = files.map((f) => f.id)
      const validSelectedFiles = selectedFiles.filter((f) =>
        existingFileIds.includes(f.id)
      )

      if (validSelectedFiles.length === 0) {
        const errorMsg =
          'Error: No valid files selected. Please upload or select existing files.'
        syncNodeData({ statusMessage: errorMsg })
        return
      }

      const updatedFiles = validSelectedFiles.map((file) => ({
        ...file,
        blobName:
          file.blobName ||
          (file.url ? new URL(file.url).pathname.split('/').pop() : file.id),
      }))

      const processingMsg = 'Processing files, please wait...'

      const documentRequest = {
        ...createAICommand(
          AICommandType.FILEMINER_PROCESS,
          AICommandStatus.STARTED,
          createNodeCommandPayload(id, {
            blobIds: updatedFiles.map((f) => f.blobName),
            userPrompt: userPrompt,
            systemPrompt: systemPrompt,
            userId: effectiveUserId,
            document: 'fileminer-analysis',
          })
        ),
        'sysagent-cmd': 'NodeFileMiner-Process-Started',
      }

      syncNodeData({
        statusMessage: processingMsg,
        selectedFiles: updatedFiles,
        blobIds: updatedFiles.map((f) => f.blobName),
        documentRequest,
      })

      setActiveTab('results')
    } catch (error) {
      console.error('Error preparing files for analysis:', error)
      const errorMsg = `Error preparing files: ${error.message || 'Unknown error'}`
      syncNodeData({ statusMessage: errorMsg })
    }
  }

  const handleGetAISuggestions = () => {
    if (!analysisResult) {
      const errorMsg =
        'Error: Please run analysis first before getting suggestions.'
      syncNodeData({ statusMessage: errorMsg })
      return
    }

    const processingMsg = 'Generating AI suggestions...'

    const documentRequest = createAICommand(
      AICommandType.FILEMINER_SUGGESTIONS,
      AICommandStatus.STARTED,
      createNodeCommandPayload(id, {
        analysisResult: analysisResult,
        document: 'fileminer-suggestions',
      })
    )

    syncNodeData({ statusMessage: processingMsg, documentRequest })
    setActiveTab('suggestions')
  }

  const clearAiSuggestions = () => {
    setAiSuggestions([])
    const newStatus = 'AI suggestions cleared'
    syncNodeData({ aiSuggestions: [], statusMessage: newStatus })
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Handle document request state changes (but don't call syncNodeData in these effects)
  useEffect(() => {
    if (!data.documentRequest) {
      isProcessingRef.current = false
      setIsGenerating(false)
      return
    }
    if (
      isCommandComplete(
        data.documentRequest,
        AICommandType.FILEMINER_PROCESS
      ) ||
      isCommandError(data.documentRequest, AICommandType.FILEMINER_PROCESS) ||
      isCommandComplete(
        data.documentRequest,
        AICommandType.FILEMINER_SUGGESTIONS
      ) ||
      isCommandError(data.documentRequest, AICommandType.FILEMINER_SUGGESTIONS)
    ) {
      isProcessingRef.current = false
      setIsGenerating(false)
    } else if (
      isCommandInProgress(
        data.documentRequest,
        AICommandType.FILEMINER_PROCESS
      ) ||
      isCommandInProgress(
        data.documentRequest,
        AICommandType.FILEMINER_SUGGESTIONS
      )
    ) {
      isProcessingRef.current = true
      setIsGenerating(true)
    } else {
      isProcessingRef.current = false
      setIsGenerating(false)
    }
  }, [data.documentRequest])

  useEffect(() => {
    if (!isProcessingRef.current && (isGenerating || isUploading)) {
      setIsGenerating(false)
      setIsUploading(false)
    }
  }, [isGenerating, isUploading])

  // Handle completion of document requests - these are user-initiated actions completing, so syncNodeData is appropriate
  useEffect(() => {
    if (!data.documentRequest) return

    if (
      isCommandComplete(data.documentRequest, AICommandType.FILEMINER_PROCESS)
    ) {
      if (data.documentRequest.response) {
        // Use setTimeout to avoid calling syncNodeData synchronously in useEffect
        setTimeout(() => {
          syncNodeData({
            analysisResult: data.documentRequest.response,
            contentMarkdown:
              data.documentRequest.formattedContent ||
              data.documentRequest.response,
            statusMessage: '',
            documentRequest: undefined,
          })
        }, 100)
        setActiveTab('results')
      }
    } else if (
      isCommandError(data.documentRequest, AICommandType.FILEMINER_PROCESS)
    ) {
      const errorMsg = data.documentRequest.error || 'Unknown error'
      setTimeout(() => {
        syncNodeData({
          statusMessage: `Error analyzing files: ${errorMsg}`,
          documentRequest: undefined,
        })
      }, 100)
      setActiveTab('results')
    }

    if (
      isCommandComplete(
        data.documentRequest,
        AICommandType.FILEMINER_SUGGESTIONS
      )
    ) {
      if (data.documentRequest.suggestions) {
        setTimeout(() => {
          syncNodeData({
            aiSuggestions: data.documentRequest.suggestions,
            statusMessage: 'AI suggestions generated.',
            documentRequest: undefined,
          })
        }, 100)
        setActiveTab('suggestions')
      }
    } else if (
      isCommandError(data.documentRequest, AICommandType.FILEMINER_SUGGESTIONS)
    ) {
      const errorMsg = data.documentRequest.error || 'Unknown error'
      setTimeout(() => {
        syncNodeData({
          statusMessage: `Error generating suggestions: ${errorMsg}`,
          documentRequest: undefined,
        })
      }, 100)
      setActiveTab('suggestions')
    }
  }, [data.documentRequest])

  // --- Data Settings logic (outgoing only) ---
  const outgoingFields = {
    userPrompt: {
      value: userPrompt,
      enabled: true,
      label: 'User Prompt',
      description: 'Custom analysis prompt provided by the user',
      permissions: { readable: true, writable: false },
    },
    systemPrompt: {
      value: systemPrompt,
      enabled: true,
      label: 'System Prompt',
      description: 'System prompt for the AI',
      permissions: { readable: true, writable: false },
    },
    selectedPresetPrompts: {
      value: selectedPresetPrompts,
      enabled: true,
      label: 'Selected Preset Prompts',
      description: 'Preset analysis types selected',
      permissions: { readable: true, writable: false },
    },
    selectedFiles: {
      value: selectedFiles.map((f) => f.filename).join(', '), // <-- Fix: show filenames as string
      enabled: true,
      label: 'Selected Files',
      description: 'Files selected for analysis (filenames)',
      permissions: { readable: true, writable: false },
    },
    analysisResult: {
      value: analysisResult,
      enabled: !!analysisResult,
      label: 'Analysis Result',
      description: 'AI-generated analysis result',
      permissions: { readable: true, writable: false },
    },
    aiSuggestions: {
      value: aiSuggestions,
      enabled: aiSuggestions && aiSuggestions.length > 0,
      label: 'AI Suggestions',
      description: 'AI-generated suggestions based on analysis',
      permissions: { readable: true, writable: false },
    },
  }

  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
    prepareExposedDataFields,
  } = useDataSettings(
    id,
    data,
    (newData) => updateNodeData(id, { ...data, ...newData }),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )

  useEffect(() => {
    // Only outgoing/exposedData, no inputLinks
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [],
      exposedData: outgoingFields,
      availableNodes: [],
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (
      JSON.stringify(dataSettings?.exposedData) !==
      JSON.stringify(outgoingFields)
    ) {
      setDataSettings(newDataSettings)
      updateNodeData(id, { ...data, dataSettings: newDataSettings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userPrompt,
    systemPrompt,
    selectedPresetPrompts,
    selectedFiles,
    analysisResult,
    aiSuggestions,
  ])

  // Copy analysis result to clipboard
  const handleCopyAnalysisResult = () => {
    const text = data.contentMarkdown || analysisResult || ''
    if (!text) return
    navigator.clipboard.writeText(text).then(
      () => {
        setCopyFeedback('Copied!')
        setTimeout(() => setCopyFeedback(''), 1200)
      },
      () => {
        setCopyFeedback('Failed')
        setTimeout(() => setCopyFeedback(''), 1200)
      }
    )
  }

  // Handle prompt changes with proper debouncing
  const handleUserPromptChange = (newPrompt: string) => {
    setUserPrompt(newPrompt)
  }

  const handleUserPromptBlur = () => {
    syncNodeData({ userPrompt })
  }

  const handleSystemPromptChange = (newPrompt: string) => {
    setSystemPrompt(newPrompt)
  }

  const handleSystemPromptBlur = () => {
    syncNodeData({ systemPrompt })
  }

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!isLocked}
      isProcessing={isGenerating || isUploading}
      status={nodeStatus}
      nodeTitle={editingTitleValue || data.label || 'FileMiner Analysis'}
      isLocked={isLocked}
      isMinimized={isMinimized}
      minimizeLevel={minimizeLevel}
      onLockChange={handleLock}
      onMinimizeChange={handleMinimize}
      onMinimizeLevelChange={handleMinimizeLevelChange}
      onTitleChange={(newTitle) => {
        setEditingTitleValue(newTitle)
        syncNodeData({ label: newTitle })
      }}
      assignedEmails={
        Array.isArray(assignedEmails)
          ? assignedEmails
          : assignedEmails
            ? assignedEmails.trim().split(/\s+/).filter(Boolean)
            : []
      }
      onAssignUsersClick={() => {
        const emailsStr = window.prompt(
          'Enter emails separated by space',
          Array.isArray(assignedEmails)
            ? assignedEmails.join(' ')
            : assignedEmails || ''
        )
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          setAssignedEmails(emailsArr)
          syncNodeData({ assignedEmails: emailsArr })
        }
      }}
      onDataSettingsClick={() => setIsDataSettingsDialogVisible(true)}
      selected={selected} // Make sure we're passing the selected prop
    >
      <div className="flex flex-col h-full w-full bg-white rounded-lg shadow">
        <div className="flex justify-between items-center px-2 py-3">
          <NodeBaseActions
            nodeId={id}
            onDelete={confirmDelete}
            onDuplicate={handleDuplicate}
            onLock={handleLock}
            onUnlock={isLocked}
            onMinimize={handleMinimize}
            onMaximize={isMinimized}
          />
          <div className="flex items-center space-x-2">
            <button
              className="px-2 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              onClick={() => setShowHelpDialog(true)}
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>

        {minimizeLevel === 'normal' && (
          <div
            className="flex flex-col h-full flex-grow px-2 pb-2 space-y-2"
            style={{ flex: 1, minHeight: 0 }}
          >            <Tabs
            defaultValue="extraction"
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as 'extraction' | 'results' | 'suggestions')
            }
            className="w-full h-full flex flex-col"
          >
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="extraction">File Extraction</TabsTrigger>
                <TabsTrigger value="results">Analysis Results</TabsTrigger>
                <TabsTrigger value="suggestions">AI Suggestions</TabsTrigger>
              </TabsList>              <TabsContent value="extraction" className={`border-none p-0 mt-2 ${styles.tabContent}`}>
                <div className={styles.extractionTab}>
                  {/* Fixed Files Section */}
                  <div className={styles.fixedFilesSection}>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Selected Files</h3>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isLocked || isUploading}
                        >
                          {isUploading ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-1" />
                          )}
                          {isUploading ? 'Uploading...' : 'Upload'}
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileInputChange}
                          disabled={isLocked || isUploading}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={toggleFileExplorer}
                          disabled={isLocked || isUploading}
                        >
                          <Folder className="h-4 w-4 mr-1" />
                          Browse Project Files
                        </Button>
                      </div>
                    </div>
                    {selectedFiles.length === 0 && (
                      <div
                        className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''} ${isUploading ? styles.dropZoneDisabled : ''}`}
                        onDragEnter={!isUploading ? handleDrag : undefined}
                        onDragOver={!isUploading ? handleDrag : undefined}
                        onDragLeave={!isUploading ? handleDrag : undefined}
                        onDrop={!isUploading ? handleDrop : undefined}
                      >
                        {isUploading ? (
                          <>
                            <RefreshCw className="h-10 w-10 mx-auto text-gray-400 mb-2 animate-spin" />
                            <p className="text-gray-600">Uploading files...</p>
                          </>
                        ) : (
                          <>
                            <FileUp className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                            <p className="text-gray-600">
                              Drag and drop files here or click upload
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    {selectedFiles.length > 0 && (
                      <ScrollArea className="h-[120px] overflow-auto nodrag border rounded-md bg-gray-50 p-2">
                        {selectedFiles.map((file) => (
                          <div
                            key={file.id}
                            className={styles.fileItemContainer}
                          >
                            <FileText className="h-5 w-5 text-gray-500" />
                            <div className={styles.fileDetails}>
                              <div className={styles.fileName}>
                                {/* Decode blobName for display if present, else fallback */}
                                {file.blobName
                                  ? decodeURIComponent(file.blobName)
                                  : file.filename}
                                {file.id.startsWith('local-') && (
                                  <Badge
                                    variant="outline"
                                    className="ml-2 text-xs"
                                  >
                                    Uploading
                                  </Badge>
                                )}
                              </div>
                              <div className={styles.fileInfo}>
                                <span>{formatFileSize(file.size)}</span>
                                <span>|</span>
                                <span>{file.contentType}</span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveFile(file.id)}
                              disabled={isLocked || isUploading}
                            >
                              <Trash className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </ScrollArea>
                    )}
                  </div>                  {/* Scrollable Parameters Section */}
                  <div className={styles.scrollableParametersSection}>
                    <div className="border rounded-md p-2">
                      <h3 className="font-medium mb-2">
                        Data Extraction Parameters
                      </h3>

                      {/* Fixed Extraction Types Selection */}
                      <div className={styles.extractionTypesSection}>
                        <label className="block text-sm font-medium mb-2">
                          Select Extraction Types:
                        </label>
                      </div>

                      {/* Scrollable Prompt Groups */}
                      <div className={styles.scrollablePromptGroups}>
                        {Object.entries(PROMPT_GROUPS).map(
                          ([groupKey, groupInfo]) => {
                            const groupPrompts = PRESET_PROMPTS.filter(
                              (prompt) => prompt.group === groupKey
                            )
                            const hasSelected = groupPrompts.some((prompt) =>
                              selectedPresetPrompts.includes(prompt.id)
                            )

                            return (
                              <div
                                key={groupKey}
                                className="border rounded-md p-3 mb-3"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium text-sm text-primary">
                                    {groupInfo.label}
                                  </h4>
                                  {hasSelected && (
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {
                                        groupPrompts.filter((p) =>
                                          selectedPresetPrompts.includes(p.id)
                                        ).length
                                      }{' '}
                                      selected
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600 mb-2">
                                  {groupInfo.description}
                                </p>
                                <div className={styles.promptSelection}>
                                  {groupPrompts.map((prompt) => (
                                    <div
                                      key={prompt.id}
                                      className={`${styles.promptChip} ${selectedPresetPrompts.includes(
                                        prompt.id
                                      )
                                          ? styles.promptChipSelected
                                          : ''
                                        }`}
                                      onClick={() =>
                                        !isLocked &&
                                        togglePresetPrompt(prompt.id)
                                      }
                                    >
                                      {prompt.label}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          }
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Fixed Custom Prompt and Advanced Options Section */}
                  <div className={styles.fixedCustomPromptSection}>
                    <div className="border rounded-md p-2">
                      <div className="mb-3">
                        <label className="block text-sm font-medium mb-1">
                          Custom Extraction Prompt:
                        </label>
                        <Textarea
                          ref={promptTextareaRef}
                          placeholder="Describe what you want to extract from these files..."
                          value={userPrompt}
                          onChange={(e) => handleUserPromptChange(e.target.value)}
                          onBlur={handleUserPromptBlur}
                          className="min-h-[80px] nodrag"
                          disabled={isLocked}
                        />
                      </div>
                      <div>
                        <details className="text-sm">
                          <summary className="font-medium cursor-pointer">
                            Advanced Options
                          </summary>
                          <div className="mt-2">
                            <label className="block text-sm font-medium mb-1">
                              System Prompt:
                            </label>
                            <Textarea
                              placeholder="System prompt for the AI..."
                              value={systemPrompt}
                              onChange={(e) =>
                                handleSystemPromptChange(e.target.value)
                              }
                              onBlur={handleSystemPromptBlur}
                              className="min-h-[60px] nodrag"
                              disabled={isLocked}
                            />
                          </div>
                        </details>
                      </div>
                    </div>
                  </div>
                </div>
                {showFileExplorer && (
                  <Dialog
                    open={showFileExplorer}
                    onOpenChange={(open) => {
                      setShowFileExplorer(open)
                      syncNodeData({ showFileExplorer: open })
                    }}
                  >
                    <DialogContent className="sm:max-w-[600px]">
                      <DialogHeader>
                        <DialogTitle>Select Project Files</DialogTitle>
                      </DialogHeader>
                      <ScrollArea className="h-[350px]">
                        {files.length > 0 ? (
                          <div className="space-y-2">
                            {files.map((file) => (
                              <div
                                key={file.id}
                                className="flex items-center justify-between p-2 border rounded-md hover:bg-gray-50"
                              >
                                <div className="flex items-center space-x-2">
                                  <FileText className="h-4 w-4" />
                                  <span className="truncate max-w-[350px]">
                                    {file.filename}
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    console.log(
                                      '🔍 Select button clicked for file:',
                                      {
                                        fileId: file.id,
                                        filename: file.filename,
                                        timestamp: new Date().toISOString(),
                                      }
                                    )
                                    handleSelectProjectFile(file)
                                  }}
                                >
                                  Select
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No project files found
                            {console.log('🔍 No files found in store:', {
                              filesLength: files.length,
                            })}
                          </div>
                        )}
                      </ScrollArea>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowFileExplorer(false)
                            syncNodeData({ showFileExplorer: false })
                          }}
                        >
                          Cancel
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </TabsContent>              <TabsContent
                value="results"
                className={`border-none p-0 mt-2 ${styles.tabContent}`}
              >
                <div className="flex justify-between items-center mb-2 ">
                  <h3 className="font-medium">Analysis Results</h3>
                  <div className="flex space-x-2 items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleCopyAnalysisResult}
                      title="Copy to clipboard"
                      disabled={!analysisResult && !data.contentMarkdown}
                    >
                      {copyFeedback ? (
                        <span className="text-xs">{copyFeedback}</span>
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGetAISuggestions}
                      disabled={isLocked || isGenerating || !analysisResult}
                    >
                      Get AI Suggestions
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleSideBySide}
                    >
                      {showSideBySide ? (
                        <PanelRightClose className="h-4 w-4" />
                      ) : (
                        <PanelRight className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>                {showSideBySide ? (
                  <div className={styles.sideBySideLayout}>
                    <div className={styles.fileListPanel}>
                      <h4 className="font-medium mb-2">Files</h4>
                      {selectedFiles.map((file) => (
                        <div key={file.id} className={styles.fileItemContainer}>
                          <FileText className="h-4 w-4 text-gray-500" />
                          <div className={styles.fileDetails}>
                            <div className={styles.fileName}>
                              {file.filename}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className={styles.contentPanel}>
                      <ScrollArea className={`${styles.scrollableContent} nodrag scrollable-content`}>
                        {analysisResult ? (
                          <Markdown
                            children={data.contentMarkdown || analysisResult}
                          />
                        ) : (
                          <div className={styles.centerContent + " text-gray-500 select-text"}>
                            Run analysis to see results here
                          </div>
                        )}
                      </ScrollArea>
                    </div>
                  </div>
                ) : (
                  <div className={`border rounded-md p-2 ${styles.contentArea}`}>
                    {analysisResult ? (
                      <ScrollArea className={`${styles.scrollableContent} nodrag scrollable-content`}>
                        <div className="select-text">
                          <Markdown
                            children={data.contentMarkdown || analysisResult}
                          />
                        </div>
                      </ScrollArea>
                    ) : (
                      <div className={styles.centerContent + " text-gray-500 select-text"}>
                        Run analysis to see results here
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>              <TabsContent value="suggestions" className={`border-none p-0 mt-2 ${styles.tabContent}`}>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium">AI Suggestions</h3>
                  {aiSuggestions.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAiSuggestions}
                    >
                      Clear Suggestions
                    </Button>
                  )}
                </div>
                <div className={`border rounded-md p-2 ${styles.contentArea}`}>
                  <ScrollArea className={styles.scrollableContent}>
                    {aiSuggestions && aiSuggestions.length > 0 ? (
                      <div className="space-y-4">
                        {aiSuggestions.map((suggestion, index) => (
                          <div key={index} className={styles.suggestionItem}>
                            <h4 className="font-medium mb-2">
                              Suggestion {index + 1}
                            </h4>
                            <div
                              className="prose max-w-none text-sm"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(suggestion),
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.centerContent + " text-gray-500"}>
                        {isGenerating
                          ? 'Generating suggestions...'
                          : 'Generate AI suggestions to see them here'}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}        {/* Always at the bottom: Process Files button and status message */}
        {minimizeLevel === 'normal' && (
          <div className={`flex flex-col gap-1 px-2 py-2 border-t bg-white ${styles.processButtonContainer}`}>
            <Button
              className="w-full"
              onClick={handleProcessFiles}
              disabled={
                isLocked ||
                isGenerating ||
                isUploading ||
                selectedFiles.length === 0 ||
                !userPrompt.trim()
              }
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin mr-2">
                    <RefreshCw className="h-4 w-4" />
                  </div>
                  Processing...
                </>
              ) : isUploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Process Files
                </>
              )}
            </Button>
            {statusMessage && !isGenerating && !isUploading && (
              <div className="text-sm text-gray-600">{statusMessage}</div>
            )}
          </div>
        )}

        {/* ...existing dialogs and modals... */}
        <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>FileMiner Help</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>
                <strong>FileMiner</strong> allows you to process files with
                AI...
              </p>
              <div className="space-y-2">
                <h4 className="font-medium">How to use:</h4>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>
                    Add files by drag & drop, upload, or selecting from project
                    files
                  </li>
                  <li>Wait for files to finish uploading to the server</li>
                  <li>Select preset analysis types or write a custom prompt</li>
                  <li>Click "Process Files" to analyze the content</li>
                  <li>View results and request AI suggestions if needed</li>
                </ol>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">Analysis Types:</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm">
                  <li>
                    <strong>Excel:</strong> Data patterns, financial analysis,
                    pivot summaries, formula extraction, markdown table
                    conversion
                  </li>
                  <li>
                    <strong>PowerPoint:</strong> Key messages, slide structure,
                    visual analysis, action items, individual slide extraction
                  </li>
                  <li>
                    <strong>Video:</strong> Transcript summary, visual content,
                    timeline events, instructions, mood and activity analysis
                  </li>
                  <li>
                    <strong>Audio:</strong> Transcription, speaker analysis,
                    sentiment, topic extraction
                  </li>
                  <li>
                    <strong>Images:</strong> Visual description, OCR text
                    extraction, object detection, chart analysis, mood and
                    activity detection
                  </li>
                  <li>
                    <strong>Text:</strong> Document structure, citations,
                    language analysis, keyword extraction, personality and
                    psychological profiling, mood analysis
                  </li>
                  <li>
                    <strong>Specialized:</strong> Legal, medical, technical,
                    research, compliance analysis
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                Note: Files are processed through the FileMiner microservice...
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowHelpDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this FileMiner node? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirmed}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* Data Settings Dialog */}
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettings}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={() => { }}
          onSyncInputLink={() => { }}
          forceShowAvailableNodes={false}
          debug={false}
        />
      </div>
    </NodeBaseContainer>
  )
}

export default memo(NodeFileMiner)
